"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { HolesLayer } from "./overlayKit/HolesLayer";
import { DevaKathaHypeCta, ctaBeatsFromWords } from "./DevaKathaHypeCta";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Karva Chauth (be77ba10) — Trimbakeshwar holes treatment, deck v6 26 Oct (19:42 re-ordered narration): 56 cards + 3 Hype phones.
// Flow Omni clips → logo-clean → PD-audio stitch 1920x1080 @24 → NVIDIA 4K. Comp 1280x720 @ 24 fps, scale=3 → 3840x2160.
// Every start/out from research/karva_chauth/overlays/gen_karva_chauth_overlays.py (WTS, no-spoiler by construction); emitted by mk_karva_tsx.py.
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";

const BASE_FS = 40, BIG_FS = 56, SMALL_FS = 28;   // Yash 2026-08-04 LAW: 40/56/28.
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -8;

const CTA_TITLE = "करवा चौथ पर छलनी से ही चाँद क्यों देखते हैं?";
const CTA_HANDLE = "@DevaKatha";
const CTA_COMMENT_TEXT = "जय गौरी शंकर 🙏 छलनी के पीछे की ये कथा पहली बार इतने विस्तार से सुनी";
// ⛔ https R2, never staticFile() — factory_4k_full pushes only src/, public/ is frozen in the image.
const CTA_SEG = (n: number) =>
  `https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/overlay_assets/karva_chauth_cta${n}_seg.mp4`;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

export const KARVA_CHAUTH_PHRASES: Phrase[] = [
  { start: 5.03, out: 9.49, lines: [[{ t: "सरगी", c: "y" }], [{ t: "पूरा", c: "w" }, { t: "दिन", c: "w" }, { t: "निर्जल", c: "r" }]] },   // P1
  { start: 18.55, out: 21.95, lines: [[{ t: "असली", c: "y" }, { t: "कहानी", c: "w" }]] },   // P2
  { start: 42.08, out: 46.58, lines: [[{ t: "श्रीकृष्ण", c: "y" }, { t: "और", c: "w" }, { t: "द्रौपदी", c: "y" }]] },   // P3
  { start: 81.37, out: 84.77, lines: [[{ t: "करक", c: "y" }, { t: "चतुर्थी", c: "w" }]] },   // P64
  { start: 97.25, out: 100.65, lines: [[{ t: "वीरवती", c: "y", big: true }]] },   // P66
  { start: 112.51, out: 115.91, lines: [[{ t: "7", c: "y", big: true }, { t: "बेटे,", c: "w" }, { t: "1", c: "y", big: true }, { t: "बेटी", c: "y" }]] },   // P67
  { start: 140.38, out: 143.78, lines: [[{ t: "दीया", c: "y" }, { t: "और", c: "w" }, { t: "छलनी", c: "y" }]] },   // P68
  { start: 154.41, out: 157.81, lines: [[{ t: "चाँद", c: "y" }, { t: "निकल", c: "w" }, { t: "आया?", c: "r" }]] },   // P69
  { start: 173.55, out: 178.18, lines: [[{ t: "तीसरा", c: "w" }, { t: "कौर", c: "w" }], [{ t: "बुलावा", c: "r" }]] },   // P70
  { start: 193.58, out: 196.98, lines: [[{ t: "झूठे", c: "r" }, { t: "चाँद", c: "w" }, { t: "का", c: "w" }, { t: "पर्दा", c: "y" }]] },   // P71
  { start: 212.08, out: 215.48, lines: [[{ t: "देवी", c: "w" }, { t: "इंद्राणी", c: "y" }]] },   // P73
  { start: 247.25, out: 250.65, lines: [[{ t: "पति", c: "w" }, { t: "ने", c: "w" }], [{ t: "आँखें", c: "y" }, { t: "खोल", c: "w" }, { t: "दीं", c: "w" }]] },   // P75
  { start: 268.13, out: 271.53, lines: [[{ t: "एक किरात", c: "y", big: true }]] },   // P77
  { start: 284.91, out: 288.81, lines: [[{ t: "गांडीव", c: "y" }, { t: "छीन", c: "r" }, { t: "लिया", c: "w" }]] },   // P79
  { start: 294.03, out: 298.67, lines: [[{ t: "किरात", c: "w" }, { t: "स्वयं", c: "w" }, { t: "महादेव", c: "y" }]] },   // P80
  { start: 320.22, out: 323.75, lines: [[{ t: "एक", c: "w" }, { t: "और", c: "w" }, { t: "स्त्री:", c: "w" }, { t: "करवा", c: "y" }]] },   // MH
  { start: 335.09, out: 338.49, lines: [[{ t: "चाँद", c: "y" }, { t: "देखना", c: "w" }, { t: "वर्जित", c: "r" }]] },   // MH
  { start: 364.88, out: 368.28, lines: [[{ t: "7", c: "y", big: true }, { t: "जन्मों", c: "w" }, { t: "का", c: "w" }, { t: "साथ", c: "y" }]] },   // P8
  { start: 373.74, out: 377.14, lines: [[{ t: "प्रजापति", c: "y" }, { t: "दक्ष", c: "y" }]] },   // P11
  { start: 389.17, out: 393.90, lines: [[{ t: "भस्म,", c: "w" }, { t: "नाग,", c: "w" }, { t: "चंद्रमा", c: "y" }]] },   // P12
  { start: 403.06, out: 406.46, lines: [[{ t: "विशाल", c: "y" }, { t: "यज्ञ", c: "w" }]] },   // P15
  { start: 420.67, out: 424.07, lines: [[{ t: "पति", c: "w" }, { t: "का", c: "w" }, { t: "नाम", c: "w" }], [{ t: "दर्ज", c: "w" }, { t: "नहीं", c: "r" }]] },   // P16
  { start: 434.76, out: 440.53, lines: [[{ t: "भाग", c: "w" }, { t: "ग़ायब:", c: "r" }], [{ t: "महादेव", c: "y" }, { t: "का", c: "w" }]] },   // P20
  { start: 451.96, out: 455.36, lines: [[{ t: "ये", c: "w" }, { t: "शरीर", c: "y" }], [{ t: "स्वीकार", c: "w" }, { t: "नहीं", c: "r" }]] },   // P21
  { start: 466.42, out: 471.40, lines: [[{ t: "जटा", c: "w" }, { t: "से", c: "w" }, { t: "वीरभद्र", c: "r" }]] },   // P23
  { start: 491.14, out: 494.54, lines: [[{ t: "फिर", c: "w" }, { t: "से", c: "w" }], [{ t: "पूरे", c: "w" }, { t: "वैरागी", c: "r" }]] },   // P27
  { start: 523.13, out: 526.58, lines: [[{ t: "वही", c: "w" }, { t: "सती,", c: "y" }], [{ t: "अब", c: "w" }, { t: "पार्वती", c: "y" }]] },   // P33
  { start: 548.76, out: 552.16, lines: [[{ t: "नाम:", c: "w" }, { t: "अपर्णा", c: "y", big: true }]] },   // P41
  { start: 575.85, out: 579.66, lines: [[{ t: "नापने", c: "r" }, { t: "वाला", c: "w" }], [{ t: "पहचान", c: "y" }, { t: "नहीं", c: "r" }, { t: "सकता", c: "w" }]] },   // P44
  { start: 585.68, out: 589.82, lines: [[{ t: "सामने", c: "w" }, { t: "स्वयं", c: "w" }, { t: "महादेव", c: "y" }]] },   // P45
  { start: 597.90, out: 601.30, lines: [[{ t: "सनातन", c: "y" }, { t: "पत्नी", c: "w" }]] },   // P46
  { start: 626.71, out: 630.11, lines: [[{ t: "त्रियुगीनारायण", c: "y", big: true }]] },   // P59
  { start: 641.02, out: 644.42, lines: [[{ t: "गौरी", c: "y" }, { t: "माँ", c: "w" }], [{ t: "यही", c: "w" }, { t: "दुल्हन", c: "y" }]] },   // P60
  { start: 648.51, out: 651.91, lines: [[{ t: "महादेव", c: "w" }, { t: "की", c: "w" }, { t: "जटाओं", c: "y" }, { t: "में", c: "w" }]] },   // P9
  { start: 664.62, out: 668.02, lines: [[{ t: "जटा", c: "w" }, { t: "का", c: "w" }, { t: "शृंगार", c: "y" }]] },   // P55
  { start: 678.03, out: 683.75, lines: [[{ t: "करवा:", c: "y" }, { t: "मिट्टी", c: "w" }, { t: "का", c: "w" }, { t: "घड़ा", c: "w" }]] },   // P82
  { start: 713.94, out: 717.60, lines: [[{ t: "कच्चे", c: "y" }, { t: "सूत", c: "w" }, { t: "का", c: "w" }, { t: "धागा", c: "w" }]] },   // P83
  { start: 723.22, out: 726.62, lines: [[{ t: "धागा अटूट", c: "y" }]] },   // P84
  { start: 742.87, out: 746.27, lines: [[{ t: "तो", c: "w" }, { t: "मैं", c: "w" }, { t: "श्राप", c: "r" }, { t: "दूँगी", c: "w" }]] },   // P85
  { start: 765.35, out: 768.75, lines: [[{ t: "धागे", c: "w" }, { t: "से", c: "w" }], [{ t: "मृत्यु", c: "r" }, { t: "को", c: "w" }, { t: "रोक", c: "w" }, { t: "दिया", c: "w" }]] },   // P87
  { start: 785.27, out: 791.74, lines: [[{ t: "चाँद", c: "y" }, { t: "ही", c: "w" }, { t: "सबसे", c: "w" }, { t: "ज़रूरी", c: "y" }]] },   // N1
  { start: 801.71, out: 805.11, lines: [[{ t: "दिन", c: "w" }, { t: "अलग,", c: "y" }], [{ t: "नियम", c: "w" }, { t: "अलग", c: "y" }]] },   // N2
  { start: 811.78, out: 816.38, lines: [[{ t: "स्नान,", c: "w" }, { t: "शृंगार,", c: "w" }, { t: "गणेश", c: "y" }, { t: "पूजा", c: "w" }]] },   // N4
  { start: 821.20, out: 824.60, lines: [[{ t: "12", c: "y", big: true }, { t: "साल,", c: "w" }, { t: "16", c: "y", big: true }, { t: "साल", c: "w" }]] },   // N5
  { start: 835.45, out: 838.85, lines: [[{ t: "न", c: "r" }, { t: "छलनी,", c: "w" }, { t: "न", c: "r" }, { t: "सरगी", c: "w" }]] },   // N7
  { start: 862.53, out: 865.93, lines: [[{ t: "पूरा", c: "w" }, { t: "शिव", c: "y" }, { t: "परिवार", c: "w" }]] },   // P90
  { start: 913.46, out: 918.05, lines: [[{ t: "पहले", c: "w" }, { t: "चाँद,", c: "y" }], [{ t: "फिर", c: "w" }, { t: "पति", c: "y" }]] },   // P93
  { start: 923.85, out: 927.25, lines: [[{ t: "पहला", c: "y" }, { t: "घूँट", c: "w" }, { t: "पानी", c: "w" }]] },   // P94
  { start: 935.64, out: 939.32, lines: [[{ t: "पति", c: "y" }, { t: "भी", c: "w" }, { t: "उपवास", c: "w" }]] },   // N8
  { start: 957.51, out: 960.91, lines: [[{ t: "कैलाश", c: "y" }, { t: "वाली", c: "w" }], [{ t: "माता", c: "w" }, { t: "पार्वती", c: "y" }]] },   // P96
  { start: 1020.49, out: 1023.89, lines: [[{ t: "छलनी के उस पार", c: "y" }], [{ t: "चाँद", c: "y" }]] },   // P101
  { start: 1048.79, out: 1052.19, lines: [[{ t: "उस पार कैलाश", c: "y" }]] },   // P104
  { start: 1090.39, out: 1093.79, lines: [[{ t: "सुहाग", c: "y" }], [{ t: "इंतज़ार", c: "w" }, { t: "से", c: "w" }, { t: "पाया", c: "w" }]] },   // P107
  { start: 1120.04, out: 1123.44, lines: [[{ t: "3", c: "y", big: true }, { t: "युगों", c: "w" }, { t: "के", c: "w" }, { t: "नारायण", c: "y" }]] },   // P109
  { start: 1135.93, out: 1139.33, lines: [[{ t: "हर छत पर,", c: "w" }], [{ t: "हर थाली में", c: "y" }]] },   // P110
  { start: 1160.99, out: 1164.39, lines: [[{ t: "केदारनाथ वाली वीडियो", c: "y" }]] },   // P112
];

const PhraseCard: React.FC<{ p: Phrase }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;
  let k = 0;
  return (
    <AbsoluteFill style={{ padding: PAD, alignItems: "flex-start", justifyContent: "flex-start" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: p.lineGap ?? LINE_GAP }}>
        {p.lines.map((line, li) => (
          <div key={li} style={{ display: "flex", alignItems: "flex-end", gap: 16, lineHeight: p.lh ?? LH }}>
            {line.map((w, wi) => {
              const delay = k++ * STAGGER;
              const prog = interpolate(t, [delay, delay + DUR], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              const fs = w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS;
              return (
                <span key={wi} style={{
                  fontFamily, fontWeight: w.big ? 900 : 800, fontSize: fs, color: col(w.c),
                  textShadow: SH, letterSpacing: p.ls,
                  transform: `translateY(${(1 - prog) * -DROP}px)`, opacity: prog,
                }}>{w.t}</span>
              );
            })}
          </div>
        ))}
      </div>
    </AbsoluteFill>
  );
};

const Teaser: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const dots = ["", ".", "..", "..."][Math.floor((frame / fps * 1.5) % 4)];
  return (
    <AbsoluteFill style={{ padding: PAD, alignItems: "flex-start", justifyContent: "flex-end" }}>
      <span style={{ fontFamily, fontWeight: 800, fontSize: SMALL_FS, color: YELLOW, textShadow: SH }}>
        आगे देखिए{dots}
      </span>
    </AbsoluteFill>
  );
};

const WatchTip: React.FC = () => (
  <AbsoluteFill style={{ padding: PAD, alignItems: "flex-end", justifyContent: "flex-end" }}>
    <div style={{ fontFamily, fontWeight: 800, fontSize: SMALL_FS, color: YELLOW, textShadow: SH }}>Watch at 1.2x</div>
    <div style={{ fontFamily, fontWeight: 700, fontSize: SMALL_FS * 0.7, color: WHITE, textShadow: SH }}>for best experience</div>
  </AbsoluteFill>
);

// 🔥 The <Sequence from={S(...)} /> durationInFrames={S(...)} values below MUST stay
// NUMERIC LITERALS — build_overlay_windows.py AND cta_segs.py both regex-scrape them.
export const KarvaChauthOverlays: React.FC = () => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      <HolesLayer holes={HOLES_DATA_URI} width={1280} height={720}>
        {KARVA_CHAUTH_PHRASES.map((p, i) => (
          <Sequence key={i} from={S(p.start)} durationInFrames={S(p.out - p.start)}>
            <PhraseCard p={p} />
          </Sequence>
        ))}
      </HolesLayer>

      <Sequence from={S(0)} durationInFrames={S(90)}>
        <Teaser />
      </Sequence>
      <Sequence from={S(0)} durationInFrames={S(5)}>
        <WatchTip />
      </Sequence>

      {/* CTA1 — लाइक 507.01 · हाइप 507.69 · कमेंट 510.56 (words without a tap: कमेंट) */}
      <Sequence from={S(505.35)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(1)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ like: 507.01, hype: 507.69 }, 505.35)} />
      </Sequence>
      {/* CTA2 — जुड 975.26 · सब्सक्राइब 976.27 (words without a tap: जुड) */}
      <Sequence from={S(973.61)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(2)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ sub: 976.27 }, 973.61)} />
      </Sequence>
      {/* CTA3 — पहुँचाइए 1168.80 */}
      <Sequence from={S(1167.14)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(3)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ share: 1168.80 }, 1167.14)} />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
